import React from "react";
import {
  AppBar,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  withStyles,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import CodeIcon from "@material-ui/icons/Code";
import RedoIcon from "@material-ui/icons/Redo";
import UndoIcon from "@material-ui/icons/Undo";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AlertSnackBar from "../../alertas/AlertSnackBar";
import ConfirmDialog from "../../alertas/Confirm";
import api from "../../../service/api";
import dataforms from "../../../service/dataforms";
import { getTenant } from "../../../libs/tenant";
import Canvas from "./Canvas";
import Inspector from "./Inspector";
import JsonDialog from "./JsonDialog";
import Palette from "./Palette";
import PreviewDialog from "./PreviewDialog";
import { createField, createColumn, fieldLabel } from "./fieldTypes";
import {
  duplicateColumn,
  duplicateField,
  reindexSignatures,
  identifierFieldIndex,
  insertColumn,
  insertField,
  moveColumn,
  moveField,
  removeColumn,
  removeField,
  updateColumn,
  updateField,
  updateFieldLabel,
} from "./layoutOps";

/**
 * Builder de formulários (versão nova).
 *
 * Três colunas — paleta, lista de campos e inspetor — no lugar do formulário
 * único do construtor antigo, onde o campo só existia depois de preenchido e,
 * uma vez criado, só dava para renomear.
 *
 * O que ele produz é **o mesmo JSON de sempre** (`layouts.options`): o DSL é
 * contrato com o preenchimento e com o PDF. Aqui muda só a forma de montar.
 */
class FormBuilderDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      saving: false,

      // Metadados do formulário (tabela `forms`).
      form_name: "",
      date_established: "",
      date_update: "",
      edit: "",
      reviewed: "",
      approved: "",
      hide_in_app: false,
      layout_id: null,

      users: [],
      users_selected: [],
      linkForms: [],

      // O layout em edição + histórico para desfazer/refazer.
      fields: [],
      selectedIndex: null,
      past: [],
      future: [],
      dirty: false,

      settingsOpen: false,
      previewOpen: false,
      jsonOpen: false,
      confirmClose: false,
      alert: false,
      alertType: "success",
      alertMessage: "",
    };
  }

  componentDidMount() {
    this.loadUsers();
    this.loadLinkForms();

    if (this.props.form != null) {
      this.loadForm();
      this.loadFormUsers();
    } else {
      // Formulário novo: já abre os dados básicos, que são obrigatórios.
      this.setState({ settingsOpen: true });
    }
  }

  /* --------------------------------- carga --------------------------------- */

  loadUsers = () => {
    api
      .get(`/getDomain?domain=${getTenant()}`)
      .then((resp) => {
        const enterpriseId = resp.data.data.result.id;
        api
          .get(`/getUsers/${enterpriseId}`)
          .then((users) => this.setState({ users: users.data || [] }))
          .catch(() => {});
      })
      .catch(() => {});
  };

  loadFormUsers = () => {
    api
      .get(`showUsers/${this.props.form.id}`)
      .then((resp) => this.setState({ users_selected: resp.data.data.result || [] }))
      .catch(() => {});
  };

  loadLinkForms = () => {
    dataforms
      .listForms(getTenant())
      .then((linkForms) => this.setState({ linkForms: linkForms || [] }))
      .catch(() => {});
  };

  loadForm = () => {
    this.setState({ loading: true, form_name: this.props.form.name });
    api
      .get("/layout/type", { params: { form_id: this.props.form.id } })
      .then((resp) => {
        let fields = [];
        try {
          fields = JSON.parse(resp.data.options) || [];
        } catch (e) {
          fields = [];
        }

        this.setState({
          fields,
          layout_id: resp.data.layout,
          date_established: resp.data.date_established || "",
          date_update: resp.data.date_update || "",
          edit: resp.data.edit || "",
          reviewed: resp.data.reviewed || "",
          approved: resp.data.approved || "",
          hide_in_app: !!resp.data.hide_in_app,
          loading: false,
        });
      })
      .catch(() => this.setState({ loading: false }));
  };

  /* ------------------------------- histórico ------------------------------- */

  // Ponto único de mutação: tudo que altera o layout passa por aqui, e é o que
  // faz desfazer/refazer funcionar sem cada ação se preocupar com isso.
  apply = (fields, selectedIndex) => {
    this.setState((state) => ({
      past: [...state.past, state.fields],
      future: [],
      fields,
      dirty: true,
      selectedIndex:
        selectedIndex === undefined ? state.selectedIndex : selectedIndex,
    }));
  };

  undo = () => {
    this.setState((state) => {
      if (state.past.length === 0) return null;
      const previous = state.past[state.past.length - 1];
      return {
        past: state.past.slice(0, -1),
        future: [state.fields, ...state.future],
        fields: previous,
        selectedIndex: null,
        dirty: true,
      };
    });
  };

  redo = () => {
    this.setState((state) => {
      if (state.future.length === 0) return null;
      const [next, ...rest] = state.future;
      return {
        past: [...state.past, state.fields],
        future: rest,
        fields: next,
        selectedIndex: null,
        dirty: true,
      };
    });
  };

  /* ------------------------------ ações do canvas --------------------------- */

  // O campo novo entra logo abaixo do selecionado (ou no fim) e já fica
  // selecionado: o inspetor abre nele para ser configurado.
  handleAddField = (type) => {
    const at =
      this.state.selectedIndex === null
        ? this.state.fields.length
        : this.state.selectedIndex + 1;
    this.apply(insertField(this.state.fields, createField(type), at), at);
  };

  handleSelect = (index) => this.setState({ selectedIndex: index });

  handleMove = (from, to) => {
    this.apply(moveField(this.state.fields, from, to), to);
  };

  handleDuplicate = (index) => {
    this.apply(duplicateField(this.state.fields, index), index + 1);
  };

  handleRemove = (index) => {
    this.apply(removeField(this.state.fields, index), null);
  };

  /* ---------------------------- ações do inspetor --------------------------- */

  handleFieldChange = (patch) => {
    this.apply(updateField(this.state.fields, this.state.selectedIndex, patch));
  };

  handleFieldLabel = (label) => {
    this.apply(updateFieldLabel(this.state.fields, this.state.selectedIndex, label));
  };

  handleColumnAdd = (type) => {
    this.apply(
      insertColumn(this.state.fields, this.state.selectedIndex, createColumn(type))
    );
  };

  handleColumnChange = (columnIndex, patch) => {
    this.apply(
      updateColumn(this.state.fields, this.state.selectedIndex, columnIndex, patch)
    );
  };

  // Troca de tipo: substitui a coluna inteira (as chaves do tipo antigo não
  // valem para o novo).
  handleColumnReplace = (columnIndex, column) => {
    const index = this.state.selectedIndex;
    const columns = (this.state.fields[index].columns || []).map((current, i) =>
      i === columnIndex ? column : current
    );
    this.apply(updateField(this.state.fields, index, { columns }));
  };

  handleColumnRemove = (columnIndex) => {
    this.apply(removeColumn(this.state.fields, this.state.selectedIndex, columnIndex));
  };

  handleColumnMove = (from, to) => {
    this.apply(moveColumn(this.state.fields, this.state.selectedIndex, from, to));
  };

  handleColumnDuplicate = (columnIndex) => {
    this.apply(duplicateColumn(this.state.fields, this.state.selectedIndex, columnIndex));
  };

  /* -------------------------------- salvamento ------------------------------ */

  // Problemas que o SUPORTE precisa ver antes de publicar. Só o nome do
  // formulário bloqueia — o resto é aviso, porque layout pela metade é um
  // estado de trabalho legítimo.
  validate = () => {
    const problems = [];

    this.state.fields.forEach((field, index) => {
      const position = `Campo ${index + 1}`;
      if (field.type === "table" && (field.columns || []).length === 0) {
        problems.push(`${position}: tabela sem colunas.`);
      }
      if (field.type === "form-link" && !field.target_form_id) {
        problems.push(`${position}: vínculo sem formulário-alvo.`);
      }
      if (field.prefill_from) {
        const link = this.state.fields[field.prefill_from.link_index];
        if (!link || link.type !== "form-link") {
          problems.push(`${position}: "preencher a partir de vínculo" não aponta para um vínculo.`);
        } else if (
          field.prefill_from.source_index === null ||
          field.prefill_from.source_index === undefined
        ) {
          problems.push(`${position}: escolha o campo de produtos de origem.`);
        }
      }
      if (field.type === "image" && !field.source) {
        problems.push(`${position}: imagem sem arquivo.`);
      }
      if (
        field.type !== "image" &&
        !(field.component && field.component.label) &&
        field.type !== "product-order" &&
        field.type !== "product-table"
      ) {
        problems.push(`${position}: sem rótulo (${fieldLabel(field)}).`);
      }
    });

    return problems;
  };

  handleSave = () => {
    if (!this.state.form_name.trim()) {
      this.setState({
        settingsOpen: true,
        alert: true,
        alertType: "error",
        alertMessage: "Dê um nome ao formulário antes de salvar.",
      });
      return;
    }

    this.setState({ saving: true });

    const payload = {
      name: this.state.form_name,
      date_established: this.state.date_established,
      date_update: this.state.date_update,
      edit: this.state.edit,
      reviewed: this.state.reviewed,
      approved: this.state.approved,
      type: this.props.type,
      hide_in_app: this.state.hide_in_app,
    };

    api
      .get(`/getDomain?domain=${getTenant()}`)
      .then((resp) => {
        const enterprise_id = resp.data.data.result.id;
        const body = { ...payload, enterprise_id };

        const request =
          this.props.form != null
            ? api.put(`/forms/${this.props.form.id}`, body).then(() => this.props.form.id)
            : api.post("/forms", body).then((r) => r.data.data.result.id);

        return request.then((formId) => this.saveLayout(formId));
      })
      .catch(() => {
        this.setState({
          saving: false,
          alert: true,
          alertType: "error",
          alertMessage: "Não foi possível salvar o formulário.",
        });
      });
  };

  saveLayout = (formId) => {
    const options = JSON.stringify(this.state.fields);
    const request =
      this.state.layout_id != null
        ? api.put(`/layouts/${this.state.layout_id}`, { options, form_id: formId })
        : api.post("/layouts", { options, form_id: formId });

    return request
      .then(() => Promise.all(this.saveUsers(formId)))
      .then(() => {
        this.setState({ saving: false, dirty: false });
        this.props.handleCloseSave();
      });
  };

  saveUsers = (formId) =>
    (this.state.users_selected || []).map((user) =>
      api.post(`vinculateUser/${formId}`, { user_id: user.id })
    );

  handleClose = () => {
    if (this.state.dirty) {
      this.setState({ confirmClose: true });
      return;
    }
    this.props.handleClose();
  };

  /* --------------------------------- render -------------------------------- */

  renderSettings = () => {
    const { classes } = this.props;

    return (
      <Dialog open fullWidth maxWidth="sm" onClose={() => this.setState({ settingsOpen: false })}>
        <DialogTitle>Dados do formulário</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome do formulário"
            value={this.state.form_name}
            onChange={(event) => this.setState({ form_name: event.target.value, dirty: true })}
            className={classes.settingsField}
          />
          <div className={classes.settingsRow}>
            <TextField
              label="Data estabelecido"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={this.state.date_established || ""}
              onChange={(event) =>
                this.setState({ date_established: event.target.value, dirty: true })
              }
            />
            <TextField
              label="Data atualizado"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={this.state.date_update || ""}
              onChange={(event) =>
                this.setState({ date_update: event.target.value, dirty: true })
              }
            />
          </div>
          <div className={classes.settingsRow}>
            <TextField
              label="Editado"
              value={this.state.edit || ""}
              onChange={(event) => this.setState({ edit: event.target.value, dirty: true })}
            />
            <TextField
              label="Revisado"
              value={this.state.reviewed || ""}
              onChange={(event) => this.setState({ reviewed: event.target.value, dirty: true })}
            />
            <TextField
              label="Aprovado"
              value={this.state.approved || ""}
              onChange={(event) => this.setState({ approved: event.target.value, dirty: true })}
            />
          </div>
          <FormControlLabel
            control={
              <Checkbox
                checked={this.state.hide_in_app}
                onChange={(event) =>
                  this.setState({ hide_in_app: event.target.checked, dirty: true })
                }
              />
            }
            label="Não exibir no app operacional"
          />
          <Autocomplete
            multiple
            options={this.state.users}
            getOptionLabel={(option) => option.name || ""}
            getOptionSelected={(option, value) => option.id === value.id}
            value={this.state.users_selected}
            onChange={(event, value) => this.setState({ users_selected: value, dirty: true })}
            renderInput={(params) => (
              <TextField {...params} label="Acesso de usuários" placeholder="Acesso de usuários" />
            )}
            className={classes.settingsField}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => this.setState({ settingsOpen: false })}>
            Pronto
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    const { classes } = this.props;
    const { fields, selectedIndex } = this.state;
    const selected = selectedIndex === null ? null : fields[selectedIndex];
    const problems = this.validate();

    return (
      <Dialog open fullScreen onClose={this.handleClose}>
        {this.state.alert && (
          <AlertSnackBar
            open={this.state.alert}
            type={this.state.alertType}
            message={this.state.alertMessage}
            onClose={() => this.setState({ alert: false })}
          />
        )}
        {this.state.confirmClose && (
          <ConfirmDialog
            title="Atencao"
            body="Ha alteracoes nao salvas neste formulario. Sair mesmo assim?"
            handleCloseConfirmT={this.props.handleClose}
            handleCloseConfirmF={() => this.setState({ confirmClose: false })}
          />
        )}
        {this.state.settingsOpen && this.renderSettings()}
        {this.state.jsonOpen && (
          <JsonDialog
            fields={fields}
            onClose={() => this.setState({ jsonOpen: false })}
            onApply={(next) => {
              this.apply(reindexSignatures(next), null);
              this.setState({ jsonOpen: false });
            }}
          />
        )}
        {this.state.previewOpen && (
          <PreviewDialog
            fields={fields}
            users={this.state.users}
            formName={this.state.form_name}
            onClose={() => this.setState({ previewOpen: false })}
          />
        )}

        <AppBar position="static" color="default" className={classes.appBar}>
          <Toolbar variant="dense">
            <IconButton edge="start" size="small" onClick={this.handleClose}>
              <CloseIcon />
            </IconButton>
            <div className={classes.titleBlock}>
              <Typography variant="subtitle1" className={classes.formName}>
                {this.state.form_name || "Novo formulário"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {fields.length} campo(s)
                {this.state.layout_id ? ` · layout ${this.state.layout_id}` : ""}
                {this.state.dirty ? " · alterações não salvas" : ""}
              </Typography>
            </div>
            <Tooltip title="Desfazer">
              <span>
                <IconButton
                  size="small"
                  disabled={this.state.past.length === 0}
                  onClick={this.undo}
                >
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refazer">
              <span>
                <IconButton
                  size="small"
                  disabled={this.state.future.length === 0}
                  onClick={this.redo}
                >
                  <RedoIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              startIcon={<CodeIcon />}
              onClick={() => this.setState({ jsonOpen: true })}
              className={classes.previewButton}
            >
              JSON
            </Button>
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => this.setState({ previewOpen: true })}
              className={classes.previewButton}
            >
              Pré-visualizar
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={this.state.saving}
              onClick={this.handleSave}
            >
              {this.state.saving && (
                <CircularProgress size={16} className={classes.savingSpinner} />
              )}
              Salvar
            </Button>
          </Toolbar>
        </AppBar>

        {this.state.loading && (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        )}

        <div className={classes.body}>
          <Paper square className={classes.left}>
            <Palette
              onAdd={this.handleAddField}
              onOpenSettings={() => this.setState({ settingsOpen: true })}
            />
          </Paper>

          <div className={classes.center}>
            <div className={classes.centerHeader}>
              <Typography variant="subtitle2">Campos do formulário</Typography>
              <Typography variant="caption" color="textSecondary">
                Arraste (ou use as setas) para reordenar. Clique para editar à direita.
              </Typography>
              {identifierFieldIndex(fields) >= 0 && (
                <Typography variant="caption" color="primary" display="block">
                  Identificador: campo {identifierFieldIndex(fields) + 1} —{" "}
                  {fieldLabel(fields[identifierFieldIndex(fields)])}
                </Typography>
              )}
            </div>
            <Divider />
            <Canvas
              fields={fields}
              selectedIndex={selectedIndex}
              onSelect={this.handleSelect}
              onMove={this.handleMove}
              onDuplicate={this.handleDuplicate}
              onRemove={this.handleRemove}
            />
            {problems.length > 0 && (
              <Paper variant="outlined" className={classes.problems}>
                <Typography variant="caption" className={classes.problemsTitle}>
                  Pendências ({problems.length})
                </Typography>
                {problems.map((problem) => (
                  <Typography key={problem} variant="caption" display="block">
                    • {problem}
                  </Typography>
                ))}
              </Paper>
            )}
          </div>

          <Paper square className={classes.right}>
            <Inspector
              field={selected}
              fields={fields}
              index={selectedIndex}
              identifierIndex={identifierFieldIndex(fields)}
              linkForms={this.state.linkForms}
              onChange={this.handleFieldChange}
              onChangeLabel={this.handleFieldLabel}
              onColumnAdd={this.handleColumnAdd}
              onColumnChange={this.handleColumnChange}
              onColumnReplace={this.handleColumnReplace}
              onColumnRemove={this.handleColumnRemove}
              onColumnMove={this.handleColumnMove}
              onColumnDuplicate={this.handleColumnDuplicate}
            />
          </Paper>
        </div>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  appBar: {
    borderBottom: "1px solid #e0e0e0",
  },
  titleBlock: {
    flex: 1,
    marginLeft: theme.spacing(1),
    minWidth: 0,
  },
  formName: {
    fontWeight: 700,
    lineHeight: 1.2,
  },
  previewButton: {
    marginRight: theme.spacing(1),
  },
  savingSpinner: {
    color: "#fff",
    marginRight: theme.spacing(1),
  },
  loading: {
    textAlign: "center",
    padding: theme.spacing(2),
  },
  body: {
    display: "flex",
    flex: 1,
    minHeight: 0,
  },
  left: {
    width: 240,
    overflowY: "auto",
    borderRight: "1px solid #e0e0e0",
  },
  center: {
    flex: 1,
    overflowY: "auto",
    backgroundColor: "#fafafa",
  },
  centerHeader: {
    padding: theme.spacing(1.5, 2),
  },
  right: {
    width: 380,
    overflowY: "auto",
    borderLeft: "1px solid #e0e0e0",
  },
  problems: {
    margin: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: "#fff8e1",
  },
  problemsTitle: {
    fontWeight: 700,
    display: "block",
  },
  settingsField: {
    marginTop: theme.spacing(2),
  },
  settingsRow: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
  },
});

export default withStyles(styles)(FormBuilderDialog);
